import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { ArrowRight, Loader2, Calendar, Plus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Local i18n for the booking section — kept inline to avoid mutating the
// global translations.js while shipping the feature.
const I18N = {
  pt: {
    overline: "Agendamento",
    title: "Escolha 2 ou 3 horários — Prof Lionel confirma o melhor.",
    subtitle:
      "Aula gratuita de 50 minutos, online. Sugira seus horários preferidos e o Lionel confirma por e-mail em até 24h.",
    labels: {
      name: "Nome completo",
      email: "E-mail",
      phone: "WhatsApp (opcional)",
      message: "Objetivo da aula (opcional)",
      slots: "Seus horários sugeridos",
      addSlot: "+ Adicionar mais uma opção",
    },
    hint: "Escolha entre 2 e 3 horários que funcionam para você (no seu fuso, horário de Brasília).",
    submit: "Solicitar aula grátis",
    submitting: "Enviando…",
    success: "Recebido! Você recebeu um e-mail. O Prof Lionel confirmará um dos horários em até 24h.",
    errors: {
      minSlots: "Sugira pelo menos 2 horários.",
      required: "Preencha nome, e-mail e 2-3 horários.",
      generic: "Erro ao enviar. Tente novamente ou fale no WhatsApp.",
    },
    micro: "Sem cartão. Sem compromisso. Seus dados são usados só para marcar essa aula.",
  },
  en: {
    overline: "Booking",
    title: "Pick 2 or 3 slots — Prof Lionel picks the best one.",
    subtitle:
      "Free 50-minute online class. Suggest your preferred slots and Lionel confirms one by email within 24h.",
    labels: {
      name: "Full name",
      email: "Email",
      phone: "WhatsApp (optional)",
      message: "Class goal (optional)",
      slots: "Your suggested times",
      addSlot: "+ Add another option",
    },
    hint: "Pick 2 to 3 times that work for you (in your local time; server assumes Brasília if no offset).",
    submit: "Request my free class",
    submitting: "Sending…",
    success: "Got it! Check your email. Prof Lionel will confirm one slot within 24h.",
    errors: {
      minSlots: "Please suggest at least 2 slots.",
      required: "Please fill your name, email and 2-3 slots.",
      generic: "Something went wrong. Try again or ping us on WhatsApp.",
    },
    micro: "No card. No commitment. Your data is used only to book this class.",
  },
  fr: {
    overline: "Réservation",
    title: "Choisis 2 ou 3 créneaux — Prof Lionel confirme le meilleur.",
    subtitle:
      "Cours gratuit de 50 minutes en ligne. Propose tes créneaux préférés et Lionel en confirme un par email sous 24h.",
    labels: {
      name: "Nom complet",
      email: "Email",
      phone: "WhatsApp (facultatif)",
      message: "Objectif du cours (facultatif)",
      slots: "Tes créneaux proposés",
      addSlot: "+ Ajouter une option",
    },
    hint: "Choisis entre 2 et 3 horaires qui te conviennent (heure locale — serveur suppose Brasília si absent).",
    submit: "Demander mon cours gratuit",
    submitting: "Envoi…",
    success: "Reçu ! Regarde tes emails. Prof Lionel confirmera un créneau sous 24h.",
    errors: {
      minSlots: "Merci de proposer au moins 2 créneaux.",
      required: "Renseigne nom, email et 2-3 créneaux.",
      generic: "Erreur d'envoi. Réessaie ou contacte-nous sur WhatsApp.",
    },
    micro: "Pas de carte. Sans engagement. Tes données ne servent qu'à réserver ce cours.",
  },
};

// Default: first slot = now + 3 days at 10:00 local. Kept outside useState
// initialiser so we can call it inside useEffect (avoids react-snap hydration
// mismatch — build-time and browser-time would differ).
const defaultSlot = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + 3 + offset);
  d.setHours(10, 0, 0, 0);
  // Return "YYYY-MM-DDTHH:MM" (input[type=datetime-local] format)
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Booking = () => {
  const { lang } = useLanguage();
  const t = useMemo(() => I18N[lang] || I18N.pt, [lang]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    website: "", // honeypot
  });
  // Start with empty slots so react-snap prerender and client hydration match.
  // We fill defaults in useEffect after mount (browser-only).
  const [slots, setSlots] = useState(["", ""]);

  useEffect(() => {
    setSlots((prev) =>
      prev.map((v, i) => (v && v.length >= 10 ? v : defaultSlot(i))),
    );
  }, []);

  const updateField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const updateSlot = (i) => (e) => {
    const v = e.target.value;
    setSlots((prev) => prev.map((s, idx) => (idx === i ? v : s)));
  };

  const addSlot = () => {
    if (slots.length >= 3) return;
    setSlots((prev) => [...prev, defaultSlot(prev.length)]);
  };

  const removeSlot = (i) => {
    if (slots.length <= 2) return;
    setSlots((prev) => prev.filter((_, idx) => idx !== i));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(t.errors.required);
      return;
    }
    const cleanSlots = slots.filter((s) => s && s.length >= 10);
    if (cleanSlots.length < 2) {
      toast.error(t.errors.minSlots);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        message: form.message || null,
        language: lang,
        slots: cleanSlots.map((s) => ({ start: s, duration_min: 30 })),
        consent: true,
        website: form.website,
      });
      setDone(true);
      toast.success(t.success);
      track("booking_submit_success", {
        language: lang,
        slot_count: cleanSlots.length,
        has_phone: Boolean(form.phone?.trim()),
        has_message: Boolean(form.message?.trim()),
      });
    } catch (err) {
      console.error(err);
      toast.error(t.errors.generic);
      track("booking_submit_error", { language: lang });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="agendar"
      data-testid="booking-section"
      className="bg-white py-24 sm:py-32 border-t border-[#e5e3db]"
    >
      <span id="agendamento" className="block -mt-24 pt-24" aria-hidden="true" />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="overline">{t.overline}</p>
          <h2
            data-testid="booking-title"
            className="font-display mt-4 text-4xl sm:text-5xl tracking-tight text-[#1e2a5a] leading-[1.05]"
          >
            {t.title}
          </h2>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#5c5c5c] max-w-md">
            {t.subtitle}
          </p>
          <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#f4f1ea] border border-[#e5e3db] px-4 py-2 text-xs text-[#5c5c5c]">
            <Calendar className="h-3.5 w-3.5 text-[#d9381e]" />
            50 min · online · grátis
          </div>
        </div>

        {done ? (
          <div
            data-testid="booking-success"
            className="lg:col-span-7 rounded-2xl bg-[#f4f1ea] border border-[#e5e3db] p-10 flex flex-col items-start"
          >
            <div className="inline-flex items-center gap-2 text-[#d9381e] mb-4">
              <Calendar className="h-5 w-5" />
              <span className="text-xs uppercase tracking-[0.18em] font-medium">
                {t.overline} ✓
              </span>
            </div>
            <h3 className="font-display text-3xl text-[#1e2a5a]">{t.success}</h3>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="booking-form"
            className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            <Field label={t.labels.name} htmlFor="booking-name" required>
              <Input
                id="booking-name"
                data-testid="booking-name-input"
                required
                value={form.name}
                onChange={updateField("name")}
                className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
              />
            </Field>
            <Field label={t.labels.email} htmlFor="booking-email" required>
              <Input
                id="booking-email"
                type="email"
                data-testid="booking-email-input"
                required
                value={form.email}
                onChange={updateField("email")}
                className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
              />
            </Field>
            <Field label={t.labels.phone} htmlFor="booking-phone" colSpan>
              <Input
                id="booking-phone"
                data-testid="booking-phone-input"
                value={form.phone}
                onChange={updateField("phone")}
                className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
              />
            </Field>

            <div className="sm:col-span-2 flex flex-col gap-3">
              <div className="flex items-baseline justify-between">
                <span className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-medium">
                  {t.labels.slots} <span className="text-[#d9381e]">*</span>
                </span>
                <span className="text-xs text-[#9c9c9c]">{t.hint}</span>
              </div>
              {slots.map((s, i) => (
                <div
                  key={i}
                  data-testid={`booking-slot-row-${i}`}
                  className="flex items-center gap-3"
                >
                  <div className="flex-1 flex items-center gap-3 rounded-md border border-[#e5e3db] bg-white px-3 h-11 focus-within:ring-2 focus-within:ring-[#1e2a5a]/20">
                    <span className="text-xs uppercase tracking-[0.14em] text-[#9c9c9c] font-medium shrink-0">
                      Opção {i + 1}
                    </span>
                    <input
                      type="datetime-local"
                      value={s}
                      onChange={updateSlot(i)}
                      data-testid={`booking-slot-input-${i}`}
                      className="flex-1 bg-transparent outline-none text-sm text-[#1e2a5a]"
                    />
                  </div>
                  {slots.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeSlot(i)}
                      data-testid={`booking-slot-remove-${i}`}
                      className="p-2 text-[#9c9c9c] hover:text-[#d9381e] transition-colors"
                      aria-label="Remover"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              {slots.length < 3 && (
                <button
                  type="button"
                  onClick={addSlot}
                  data-testid="booking-add-slot"
                  className="self-start inline-flex items-center gap-2 text-sm text-[#1e2a5a] hover:text-[#d9381e] transition-colors mt-1"
                >
                  <Plus className="h-4 w-4" />
                  {t.labels.addSlot}
                </button>
              )}
            </div>

            <Field label={t.labels.message} htmlFor="booking-message" colSpan>
              <Textarea
                id="booking-message"
                data-testid="booking-message-input"
                rows={3}
                value={form.message}
                onChange={updateField("message")}
                className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20"
              />
            </Field>

            {/* Honeypot */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={updateField("website")}
              style={{
                position: "absolute",
                left: "-10000px",
                width: "1px",
                height: "1px",
                opacity: 0,
                pointerEvents: "none",
              }}
            />

            <div className="sm:col-span-2 pt-2">
              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={submitting}
                className="group inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-3.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.submitting}
                  </>
                ) : (
                  <>
                    {t.submit}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="mt-4 text-xs leading-relaxed text-[#5c5c5c] max-w-xl">
                {t.micro}
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

const Field = ({ label, htmlFor, children, colSpan, required }) => (
  <label
    htmlFor={htmlFor}
    className={`flex flex-col gap-2 ${colSpan ? "sm:col-span-2" : ""}`}
  >
    <span className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-medium">
      {label}
      {required ? <span className="text-[#d9381e]"> *</span> : null}
    </span>
    {children}
  </label>
);

export default Booking;
